import React, { useEffect, useRef, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import { createPortal } from 'react-dom';
import useAppStore from '../../store';
import { ROUTING_EVENTS } from '../../mocks/channels';

const FOCUSABLE = 'button, input, select, textarea, a[href], [tabindex]:not([tabindex="-1"])';

/**
 * Minimal modal primitive (T1.2 contract, channels-scoped until T1 lands):
 * portal to body, ESC + backdrop close, focus trap, initial focus.
 */
function AddRoutingRuleModal({ open, onClose }) {
  const agents = useAppStore((state) => state.agents);
  const channels = useAppStore((state) => state.channels);
  const addRoutingRule = useAppStore((state) => state.addRoutingRule);

  const [agentId, setAgentId] = useState('');
  const [event, setEvent] = useState('');
  const [channelType, setChannelType] = useState('');
  const [target, setTarget] = useState('');
  const [error, setError] = useState('');

  const dialogRef = useRef(null);
  const firstFieldRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    firstFieldRef.current?.focus();
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (domEvent) => {
      if (domEvent.key === 'Escape') {
        onClose();
        return;
      }

      if (domEvent.key !== 'Tab') return;

      const focusable = dialogRef.current?.querySelectorAll(FOCUSABLE);
      if (!focusable?.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (domEvent.shiftKey && document.activeElement === first) {
        domEvent.preventDefault();
        last.focus();
      } else if (!domEvent.shiftKey && document.activeElement === last) {
        domEvent.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleSubmit = (domEvent) => {
    domEvent.preventDefault();

    if (!agentId || !event || !channelType || !target.trim()) {
      setError('Every field is required — pick agent, event, channel, and a destination.');
      return;
    }

    addRoutingRule({
      agentId: Number(agentId),
      event,
      channelType,
      target: target.trim(),
      enabled: true
    });

    setAgentId('');
    setEvent('');
    setChannelType('');
    setTarget('');
    setError('');
    onClose();
  };

  return createPortal(
    (
      <div className="modal-backdrop" onClick={onClose}>
        <div
          aria-modal="true"
          className="modal"
          onClick={(domEvent) => domEvent.stopPropagation()}
          ref={dialogRef}
          role="dialog"
        >
          <header className="modal__head">
            <h2>New routing rule</h2>
            <button className="modal__close" onClick={onClose} type="button">
              <FiIcons.FiX />
            </button>
          </header>

          <form className="modal__body" onSubmit={handleSubmit}>
            <label>
              <span>Agent</span>
              <select
                onChange={(domEvent) => setAgentId(domEvent.target.value)}
                ref={firstFieldRef}
                value={agentId}
              >
                <option value="">Select agent…</option>
                {agents.map((agent) => (
                  <option key={agent.id} value={agent.id}>{agent.name} — {agent.role}</option>
                ))}
              </select>
            </label>

            <label>
              <span>When</span>
              <select onChange={(domEvent) => setEvent(domEvent.target.value)} value={event}>
                <option value="">Select event…</option>
                {ROUTING_EVENTS.map((candidate) => (
                  <option key={candidate.value} value={candidate.value}>{candidate.label}</option>
                ))}
              </select>
            </label>

            <label>
              <span>Notify via</span>
              <select
                onChange={(domEvent) => setChannelType(domEvent.target.value)}
                value={channelType}
              >
                <option value="">Select channel…</option>
                {channels.map((channel) => (
                  <option key={channel.id} value={channel.type}>{channel.name}</option>
                ))}
              </select>
            </label>

            <label>
              <span>Destination</span>
              <input
                onChange={(domEvent) => setTarget(domEvent.target.value)}
                placeholder="@handle, phone number, or email"
                type="text"
                value={target}
              />
            </label>

            {error && <p className="modal__error">{error}</p>}

            <footer className="modal__actions">
              <button className="ghost-button" onClick={onClose} type="button">Cancel</button>
              <button className="primary-button" type="submit">Create rule</button>
            </footer>
          </form>
        </div>
      </div>
    ),
    document.body
  );
}

export default AddRoutingRuleModal;
